import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getOrgPermissions } from '../../utils/permissions';
import { getCatalog, listEngagements, STATUS_LABELS, apiError } from './engagementsApi';
import RequestWizard from './RequestWizard';
import './Engagements.css';

const OPEN = ['REQUESTED', 'ISSUED', 'AWAITING_COUNTERSIGN', 'ACTIVE', 'ON_HOLD', 'SUSPENDED'];

export function StatusPill({ status, labels }) {
  const map = labels || STATUS_LABELS;
  return <span className={`eng-pill eng-pill-${String(status || '').toLowerCase()}`}>{map[status] || status}</span>;
}

/** Service Onboarding home: the org's engagements (one card each) and the services it can still request. */
export default function EngagementsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isOrgAdmin, isPartner } = getOrgPermissions();
  const canRequest = isOrgAdmin && !isPartner;

  const [catalog, setCatalog] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [wizardFor, setWizardFor] = useState(null); // serviceType or '' when open with no preselect

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [c, l] = await Promise.all([getCatalog(), listEngagements()]);
      setCatalog(c);
      setItems(l);
    } catch (e) {
      setError(apiError(e, t('Engagements.loadError', 'Could not load your engagements.')));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openTypes = new Set(items.filter((e) => OPEN.includes(e.status)).map((e) => e.serviceType));
  const available = catalog.filter((c) => !openTypes.has(c.serviceType));

  return (
    <div className="eng-page">
      <div className="eng-header">
        <div>
          <h2><i className="fas fa-file-signature" style={{ marginRight: 10, color: '#1d4ed8' }} />
            {t('Engagements.title', 'Service Onboarding')}</h2>
          <div className="eng-sub">
            {t('Engagements.subtitle', 'Request a security service, complete the intake, sign the agreements in CATAAM, and the service switches on automatically once every document is executed.')}
          </div>
        </div>
        {canRequest && (
          <button className="eng-btn-primary" onClick={() => setWizardFor('')}>
            <i className="fas fa-plus" /> {t('Engagements.request', 'Request a service')}
          </button>
        )}
      </div>
      {error && <p className="eng-error">{error}</p>}

      {loading ? (
        <div className="eng-empty"><i className="fas fa-spinner fa-spin" /> {t('common.loading', 'Loading…')}</div>
      ) : (
        <>
          <div className="eng-section-title">{t('Engagements.yours', 'Your engagements')}</div>
          {items.length === 0 ? (
            <div className="eng-empty">{t('Engagements.none', 'No engagements yet. Pick a service below to get started.')}</div>
          ) : (
            <div className="eng-grid">
              {items.map((e) => {
                const total = e.requiredTotal || 0;
                const signed = e.requiredSigned || 0;
                return (
                  <div key={e.id} className="eng-card" onClick={() => navigate(`/dashboard/engagements/${e.id}`)}>
                    <div className="eng-card-head">
                      <div>
                        <div className="eng-card-title">{e.serviceLabel}</div>
                        <div className="eng-card-ref">{e.reference} · {t('Engagements.requested', 'requested')} {String(e.requestedAt || '').slice(0, 10)}</div>
                      </div>
                      <StatusPill status={e.status} />
                    </div>
                    {total > 0 && (
                      <>
                        <div className="eng-progress"><div style={{ width: `${Math.round((signed / total) * 100)}%` }} /></div>
                        <div className="eng-card-foot">
                          <span>{signed}/{total} {t('Engagements.docsExecuted', 'documents executed')}</span>
                          {e.pendingClientSignatures > 0 && (
                            <span style={{ color: '#92400e', fontWeight: 600 }}>
                              <i className="fas fa-pen-nib" /> {e.pendingClientSignatures} {t('Engagements.toSign', 'to sign')}
                            </span>
                          )}
                        </div>
                      </>
                    )}
                    {e.status === 'REQUESTED' && (
                      <div className="eng-card-foot"><span>{t('Engagements.underReview', 'Cataam is reviewing your intake and preparing the agreements.')}</span></div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {available.length > 0 && (
            <>
              <div className="eng-section-title">{t('Engagements.available', 'Available services')}</div>
              <div className="eng-grid">
                {available.map((c) => (
                  <div key={c.serviceType} className="eng-card eng-card-static">
                    <div className="eng-card-title">{c.name}</div>
                    <div className="eng-card-desc">{c.description}</div>
                    <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{t('Engagements.requiresDocs', 'Documents to sign:')}</div>
                    <ul className="eng-doclist">
                      {(c.requiredDocuments || []).map((d) => <li key={d.templateCode}>{d.name}{d.required ? '' : ` (${t('Engagements.optional', 'optional')})`}</li>)}
                    </ul>
                    <div className="eng-card-foot">
                      <span />
                      {canRequest
                        ? <button className="eng-btn eng-btn-sm" onClick={() => setWizardFor(c.serviceType)}>{t('Engagements.requestThis', 'Request')} <i className="fas fa-arrow-right" /></button>
                        : <span className="eng-help">{t('Engagements.adminOnly', 'An organisation admin can request this.')}</span>}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}

      {wizardFor !== null && (
        <RequestWizard
          catalog={catalog}
          preselect={wizardFor}
          onClose={() => setWizardFor(null)}
          onCreated={(e) => { setWizardFor(null); navigate(`/dashboard/engagements/${e.id}`); }}
        />
      )}
    </div>
  );
}
